import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PILLARS } from "@/lib/content";
import { SectionLabel, RevealLines, FadeUp, EASE } from "../site/motion";

const Panel = ({ p, active, onEnter }) => (
  <motion.div
    layout
    onMouseEnter={onEnter}
    data-testid={`pillar-panel-${p.id}`}
    transition={{ layout: { duration: 0.8, ease: EASE } }}
    className={`group relative h-[460px] overflow-hidden rounded-[24px] border border-cream/10 lg:h-[560px] ${active ? "lg:flex-[2]" : "lg:flex-1"}`}
  >
    <img src={p.img} alt={p.title} className={`duotone absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(.16,1,.3,1)] ${active ? "scale-100" : "scale-110"}`} />
    <div className={`absolute inset-0 transition-colors duration-700 bg-gradient-to-t from-skaris-ink via-skaris-ink/60 to-skaris-ink/10 ${active ? "" : "lg:bg-none lg:bg-skaris-ink/75"}`} />
    <div className="relative flex h-full flex-col justify-between p-7 md:p-9">
      <div className="flex items-start justify-between">
        <span className="font-mono text-xs text-skaris-sky">{p.n}</span>
        <ArrowUpRight className={`h-6 w-6 text-cream transition-transform duration-500 ${active ? "rotate-0" : "rotate-45"}`} />
      </div>
      <div>
        <h3 className={`font-display text-3xl font-semibold uppercase leading-[0.95] tracking-tight text-cream transition-[font-size] duration-500 ${active ? "md:text-4xl lg:text-5xl" : "lg:text-2xl"}`}>{p.title}</h3>
        <div className={`grid transition-[grid-template-rows,opacity] duration-700 ${active ? "grid-rows-[1fr] opacity-100" : "grid-rows-[1fr] opacity-100 lg:grid-rows-[0fr] lg:opacity-0"}`}>
          <div className="overflow-hidden">
            <p className="mt-5 max-w-md font-body text-sm leading-relaxed text-cream/80 md:text-base">{p.short}</p>
            <ul className="mt-6 flex max-w-lg flex-wrap gap-2">
              {p.points.map((pt) => (
                <li key={pt} className="rounded-full border border-cream/20 bg-skaris-ink/40 px-3 py-1.5 font-body text-xs text-cream/85 backdrop-blur">{pt}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

export const Pillars = () => {
  const [active, setActive] = useState(0);
  return (
    <section id="pillars" data-testid="pillars-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel n="(02)">What we do</SectionLabel>
            <RevealLines inView as="h2" lines={["Three disciplines.", "One delivery team."]}
              className="mt-6 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl lg:text-7xl"
              lineClass={(i) => (i === 1 ? "text-cream/55" : "")} />
          </div>
          <FadeUp className="lg:col-span-4 lg:col-start-9">
            <p className="font-body text-base text-cream/75">Whether you need a platform built, an operation digitised, or intelligence embedded — the same team owns it from first workshop to post-launch support.</p>
            <Link to="/services" data-testid="pillars-view-services" className="link-underline mt-5 inline-block font-mono text-xs uppercase tracking-[0.2em] text-cream">View all services →</Link>
          </FadeUp>
        </div>
        <FadeUp className="mt-14 flex flex-col gap-4 lg:flex-row">
          {PILLARS.map((p, i) => <Panel key={p.id} p={p} active={active === i} onEnter={() => setActive(i)} />)}
        </FadeUp>
      </div>
    </section>
  );
};
